<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useManufacturersStore } from './stores/manufacturers';
import ManufacturerDetails from './ManufacturerDetails.vue';

defineEmits(['close']);

const store = useManufacturersStore();
const cfg   = window.ebusManufacturers || {};
const i18n  = cfg.i18n || {};

const mapContainerRef = ref(null);
let map2 = null;

function initMap() {
    if (!mapContainerRef.value || !window.google?.maps) return;

    const center = cfg.mapCenter || { lat: -12.046373, lng: -74.042755 };

    map2 = new google.maps.Map(mapContainerRef.value, {
        center,
        zoom: 3,
        minZoom: 2,
        maxZoom: 4,
        disableDefaultUI: true,
        scrollwheel: true,
        disableDoubleClickZoom: true,
        backgroundColor: '#EDEDED',
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles: [
            { featureType: 'water', elementType: 'geometry', stylers: [{ visibility: 'on' }, { color: '#ededed' }, { saturation: -100 }] },
            { featureType: 'landscape', elementType: 'geometry', stylers: [{ visibility: 'on' }, { lightness: -10 }, { saturation: -80 }] },
            { featureType: 'road', stylers: [{ visibility: 'off' }] },
            { featureType: 'poi', stylers: [{ visibility: 'off' }] },
            { featureType: 'administrative', stylers: [{ visibility: 'off' }] },
            { featureType: 'administrative.country', elementType: 'geometry.stroke', stylers: [{ visibility: 'on' }, { color: '#777777' }] },
            { elementType: 'labels', stylers: [{ visibility: 'off' }] },
        ],
    });

    const wkt = cfg.wkt || '';
    if (wkt) {
        const match = wkt.match(/POLYGON\s*\(\((.*?)\)\)/);
        if (match) {
            const coords = match[1].split(', ').map(coord => {
                const [lng, lat] = coord.split(' ').map(Number);
                return { lat, lng };
            });
            new google.maps.Polygon({
                paths: coords,
                strokeColor: '#EDEDED',
                strokeOpacity: 1,
                strokeWeight: 1,
                fillColor: '#EDEDED',
                fillOpacity: 1,
                map: map2,
            });
        }
    }
}

onMounted(initMap);

onUnmounted(() => {
    map2 = null;
});

function extendBoundsWithGeometry(geometry, bounds) {
    const type = geometry.getType();
    if (type === 'Point') {
        bounds.extend(geometry.get());
    } else if (type === 'LinearRing' || type === 'LineString' || type === 'MultiPoint') {
        geometry.getArray().forEach(p => bounds.extend(p));
    } else {
        geometry.getArray().forEach(g => extendBoundsWithGeometry(g, bounds));
    }
}

watch(() => store.currentManufacturer, m => {
    if (!map2) return;
    map2.data.forEach(f => map2.data.remove(f));

    const baseUrl  = cfg.geodataUrl || '';
    const isoCodes = (m?.iso_codes || []).filter(iso => iso && baseUrl);

    if (!isoCodes.length) return;

    const bounds = new google.maps.LatLngBounds();
    let pending  = isoCodes.length;

    const featureListener = map2.data.addListener('addfeature', e => {
        extendBoundsWithGeometry(e.feature.getGeometry(), bounds);
    });

    isoCodes.forEach(iso => {
        map2.data.loadGeoJson(baseUrl + iso + '.geo.json', null, () => {
            pending--;
            if (pending === 0) {
                google.maps.event.removeListener(featureListener);
                if (!bounds.isEmpty()) {
                    map2.fitBounds(bounds);
                }
            }
        });
    });

    map2.data.setStyle({ fillColor: '#e53e3e', strokeWeight: 0 });
});

async function onListClick(m, idx) {
    store.activeIndex         = idx;
    store.currentManufacturer = null;
    await store.loadDetail(m.id);
}

function navigate(dir) {
    const items = store.manufacturers;
    if (!items.length) return;
    const next            = ((store.activeIndex + dir) + items.length) % items.length;
    store.activeIndex     = next;
    store.currentManufacturer = null;
    store.loadDetail(items[next].id);
}
</script>

<template>
  <div class="manufactors__container manufactors__container--dashboard" style="display: flex;">
    <div class="manufactors__content">

      <!-- Manufacturer list -->
      <div class="manufactors__list">
        <p class="list__label manufactors__list__title">{{ i18n.manufacturers || 'Fabricantes' }}</p>
        <ul class="list">
          <li
            v-for="(m, idx) in store.manufacturers"
            :key="m.id"
            :class="['list__item', `manufactor-item--${m.id}`, { 'list__item--active': idx === store.activeIndex }]"
            @click="onListClick(m, idx)"
          >{{ m.title }}</li>
        </ul>
      </div>

      <!-- Dashboard -->
      <div class="manufactors__dashboard">
        <div v-if="store.currentManufacturer" class="dashboard__status">
          <img
            :src="store.currentManufacturer.in_latin_america ? cfg.iconOperationUrl : cfg.iconNoOperationUrl"
            class="dashboard__status_icon"
          />
          {{ store.currentManufacturer.in_latin_america ? i18n.inOperation : i18n.noOperation }}
        </div>

        <div class="dashboard__grid">
          <ManufacturerDetails v-if="store.currentManufacturer" />

          <div class="dashboard__map-wrapper">
            <div class="dashboard__map">
              <div ref="mapContainerRef" style="height:400px;width:100%;"></div>
            </div>
            <img
              class="dashboard__zebra-stamp"
              :src="cfg.zebraLogoUrl"
              :style="{ display: store.currentManufacturer?.zebra_stamp ? 'block' : 'none' }"
            />
          </div>

          <div class="dashboard__models-wrapper">
            <div v-if="store.currentManufacturer?.models?.length" class="dashboard__models">
              <table class="models__table">
                <thead>
                  <tr>
                    <th>{{ i18n.models || 'Modelos' }}</th>
                    <th>{{ i18n.sizes || 'Tamanhos' }}</th>
                    <th>{{ i18n.autonomy || 'Autonomia (km)' }}</th>
                    <th>{{ i18n.capacity || 'Capacidade' }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="model in store.currentManufacturer.models" :key="model.name">
                    <td>
                      <a v-if="model.more_info" :href="model.more_info" target="_blank">{{ model.name }}</a>
                      <span v-else>{{ model.name }}</span>
                    </td>
                    <td>
                      <ul class="manufactor__sizes">
                        <li
                          v-for="s in (model.sizes || [])"
                          :key="s"
                          :class="`bus-size bus-size--${s}`"
                        ></li>
                      </ul>
                    </td>
                    <td>{{ model.autonomy || '' }}</td>
                    <td>{{ model.capacity || '' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-if="i18n.citiesNote" v-html="i18n.citiesNote"></p>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="manufactors__actions">
        <div class="action action--forward" @click="navigate(1)">
          <img class="action__icon" :src="cfg.rightArrowUrl" />
        </div>
        <div class="action action--backward" @click="navigate(-1)">
          <img class="action__icon" :src="cfg.leftArrowUrl" />
        </div>
        <div class="action action--grid" @click="$emit('close')">
          <img class="action__icon" :src="cfg.backToDashboardUrl" />
        </div>
      </div>

    </div>
  </div>
</template>
